import React, { useEffect, useState } from "react";
import {
  Alert, Button, Card, Col, Form, Input, Row, Select, Space, Spin, Tag, Typography,
} from "antd";
import { SaveOutlined, ArrowLeftOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

export default function FrmUsuarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esEdicion = Boolean(id);

  const [form] = Form.useForm();
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [roles, setRoles] = useState([]);

  // Cargar roles
  useEffect(() => {
    axios.get(`${API_URL}/Rol`).then(r => {
      const lista = Array.isArray(r.data?.$values) ? r.data.$values
        : Array.isArray(r.data) ? r.data : [];
      setRoles(lista);
    }).catch(() => {});
  }, []);

  // Si edición, cargar datos del usuario
  useEffect(() => {
    if (!esEdicion) return;
    const cargar = async () => {
      try {
        setCargando(true);
        const resp = await axios.get(`${API_URL}/Usuario/${id}`);
        const d = resp.data;
        form.setFieldsValue({
          nombreUsuario: d.nombreUsuario,
          nombre: d.nombre,
          apellido: d.apellido,
          email: d.email,
          rolId: d.rolId,
        });
      } catch {
        setResultado({ tipo: "error", texto: "No se pudo cargar el usuario." });
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [id, esEdicion]);

  const onGuardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);
      setResultado(null);

      if (esEdicion) {
        await axios.put(`${API_URL}/Usuario/${id}`, {
          nombreUsuario: values.nombreUsuario,
          email: values.email,
          nombre: values.nombre,
          apellido: values.apellido,
          rolId: values.rolId ?? null,
        });
        setResultado({ tipo: "success", texto: "✅ Usuario actualizado correctamente." });
      } else {
        await axios.post(`${API_URL}/Usuario`, {
          nombreUsuario: values.nombreUsuario,
          password: values.password,
          email: values.email,
          nombre: values.nombre,
          apellido: values.apellido,
          perfil: 1,
          rolId: values.rolId ?? null,
        });
        navigate("/dashboard/usuarios");
      }
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.mensaje ?? err?.response?.data?.detalle ?? err?.message ?? "Error al guardar.";
      setResultado({ tipo: "error", texto: `❌ ${msg}` });
    } finally {
      setGuardando(false);
    }
  };

  const rolSeleccionado = roles.find(r => r.id === form.getFieldValue("rolId"));

  return (
    <div style={{ padding: 16, maxWidth: 680 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/usuarios")}>
          Volver
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 8 }} />
          {esEdicion ? "Editar usuario" : "Nuevo usuario"}
        </Title>
      </Space>

      {resultado && (
        <Alert type={resultado.tipo} message={resultado.texto} showIcon closable
          onClose={() => setResultado(null)} style={{ marginBottom: 16 }} />
      )}

      <Spin spinning={cargando}>
        <Card>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Nombre" name="nombre"
                  rules={[{ required: true, message: "Ingrese el nombre." }]}>
                  <Input placeholder="Ej: Carlos" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Apellido" name="apellido"
                  rules={[{ required: true, message: "Ingrese el apellido." }]}>
                  <Input placeholder="Ej: González" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Nombre de usuario" name="nombreUsuario"
                  rules={[
                    { required: true, message: "Ingrese el nombre de usuario." },
                    { min: 4, message: "Mínimo 4 caracteres." },
                    { pattern: /^[a-zA-Z0-9._]+$/, message: "Solo letras, números, punto y guion bajo." },
                  ]}>
                  <Input prefix="@" placeholder="usuario.finca" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Correo electrónico" name="email"
                  rules={[{ type: "email", message: "Ingrese un email válido." }]}>
                  <Input placeholder="correo@ejemplo.com" />
                </Form.Item>
              </Col>
            </Row>

            {!esEdicion && (
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Contraseña inicial" name="password"
                    rules={[
                      { required: true, message: "Ingrese la contraseña." },
                      { min: 6, message: "Mínimo 6 caracteres." },
                    ]}>
                    <Input.Password placeholder="Mínimo 6 caracteres" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Confirmar contraseña" name="confirmar"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "Confirme la contraseña." },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value)
                            return Promise.resolve();
                          return Promise.reject(new Error("Las contraseñas no coinciden."));
                        },
                      }),
                    ]}>
                    <Input.Password placeholder="Repita la contraseña" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item label="Rol / Nivel de acceso" name="rolId">
              <Select
                allowClear
                placeholder="Seleccione el rol del usuario..."
                optionFilterProp="children"
              >
                {roles.map(r => (
                  <Select.Option key={r.id} value={r.id}>
                    <Space>
                      {r.esAdministrador && <Tag color="red">Admin</Tag>}
                      {r.nombre}
                      {r.descripcion && (
                        <Text type="secondary" style={{ fontSize: 11 }}>— {r.descripcion}</Text>
                      )}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Vista previa de permisos del rol seleccionado */}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const rId = getFieldValue("rolId");
                const rol = roles.find(r => r.id === rId);
                if (!rol) return null;
                const accesos = Array.isArray(rol.accesos?.$values)
                  ? rol.accesos.$values
                  : Array.isArray(rol.accesos) ? rol.accesos : [];
                if (accesos.length === 0) return null;
                return (
                  <Card
                    size="small"
                    style={{ background: "#f6ffed", borderColor: "#b7eb8f", marginBottom: 12 }}
                    title={<Text style={{ color: "#52c41a" }}>🔐 Permisos del rol "{rol.nombre}"</Text>}
                  >
                    {accesos.map(a => (
                      <div key={a.modulo} style={{ marginBottom: 4 }}>
                        <Text strong style={{ textTransform: "capitalize" }}>{a.modulo}: </Text>
                        {a.puedeVer && <Tag color="blue">Ver</Tag>}
                        {a.puedeCrear && <Tag color="green">Crear</Tag>}
                        {a.puedeEditar && <Tag color="orange">Editar</Tag>}
                        {a.puedeEliminar && <Tag color="red">Eliminar</Tag>}
                        {!a.puedeVer && !a.puedeCrear && !a.puedeEditar && !a.puedeEliminar && (
                          <Tag color="default">Sin acceso</Tag>
                        )}
                      </div>
                    ))}
                  </Card>
                );
              }}
            </Form.Item>
          </Form>
        </Card>

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Space>
            <Button onClick={() => navigate("/dashboard/usuarios")}>Cancelar</Button>
            <Button type="primary" icon={<SaveOutlined />}
              onClick={onGuardar} loading={guardando}>
              {esEdicion ? "Guardar cambios" : "Crear usuario"}
            </Button>
          </Space>
        </div>
      </Spin>
    </div>
  );
}
